// Section 25 (M5d): the steward's crew plan. Fills jobs in priority order from the free workers
// (healthy adults below the retirement age; children only in an emergency and only when the
// child-labour policy allows), keeps people in their jobs where it can (skill, planted plots),
// and never leaves food, water, wood and (Autumn and Winter) fuel uncovered while anyone can
// work. Sick, injured and held people keep their posts; retirees who already work volunteer.
import { canWork } from '../../phases/shared/eligibility';
import { num } from '../../phases/shared/value';
import { buildingCount, isWorking, skillOf } from '../../core/queries';
import type { JobId, Person, PersonId, Resource } from '../../core/types';
import {
  ableNow,
  capacityOf,
  carry,
  isKid,
  isRetired,
  plotsWanted,
  rate,
  seasonOf,
  totalPlots,
  warmthDemand,
  type StewardCtx,
} from './model';

/** Candidate plans the steward compares by projected fragility (choose.ts). */
export type Variant = 'balanced' | 'invest' | 'essentials' | 'recover';

export interface Materials {
  wood: number;
  stone: number;
}

/** What the build plan needs from the crew this season. */
export interface BuildNeeds {
  /** An order can take build points this season (materials paid or on hand). */
  buildable: boolean;
  /** Materials still to be paid by queued orders (wood, stone). */
  owed: Materials;
  /** Materials of the first wanted building the stock cannot pay for yet (to gather). */
  wanted: Materials;
}

export interface Plan {
  variant: Variant;
  jobs: Map<PersonId, JobId>;
  /** Something essential is short within the horizon under this plan. */
  risk: boolean;
  /** Essentials left short: resources with a shortfall in the horizon. */
  short: ('food' | 'water' | 'warmth')[];
}

interface Planner {
  sc: StewardCtx;
  variant: Variant;
  jobs: Map<PersonId, JobId>;
  /** Healthy, assignable, not yet placed; sorted by id. */
  free: Person[];
  /** Children the child-labour policy lets work; used only to cover essentials. */
  kids: Person[];
  /**
   * Resting retirees, asked to volunteer (Section 18 "may volunteer") only for the light posts
   * where an elder's skill counts most: healer, teacher, caretaker (Section 15 "elders are the
   * natural teachers and caretakers"). M7.
   */
  volunteers: Person[];
  /** holders() by job, dropped by place() when a job's holders change (Section 2 budget). */
  held: Map<JobId, readonly Person[]>;
}

/** Everyone planned into a job, in id order. Read-only (cached until the job's holders change). */
function holders(pl: Planner, job: JobId): readonly Person[] {
  let list = pl.held.get(job);
  if (list === undefined) {
    list = pl.sc.people.filter((p) => pl.jobs.get(p.id) === job);
    pl.held.set(job, list);
  }
  return list;
}

/**
 * The best skill a person has in another job the settlement keeps a specialist for (M7): among
 * equally skilled candidates for a common job, the steward takes the one whose rarer skill is
 * not needed elsewhere.
 */
function specialism(pl: Planner, p: Person, job: JobId): number {
  let best = 0;
  for (const other of pl.sc.cfg.steward.specialistJobs as JobId[])
    if (other !== job) best = Math.max(best, skillOf(p, other));
  return best;
}

/**
 * Skill, with a bonus for the current holder so crews are not reshuffled for a small gain
 * (M7: a bonus, not a rule, so a far better worker displaces a novice holder), then the least
 * specialised, then strength, then id (stable, deterministic).
 */
function ranked(pl: Planner, pool: readonly Person[], job: JobId): Person[] {
  const bonus = pl.sc.cfg.steward.holderSkillBonus;
  const score = (p: Person): number => skillOf(p, job) + (p.job === job ? bonus : 0);
  return pool
    .filter((p) => canWork(pl.sc.ctx, p, job))
    .sort(
      (a, b) =>
        score(b) - score(a) ||
        specialism(pl, a, job) - specialism(pl, b, job) ||
        b.strength - a.strength ||
        (a.id < b.id ? -1 : 1),
    );
}

function place(pl: Planner, p: Person, job: JobId): void {
  const before = pl.jobs.get(p.id);
  if (before !== undefined) pl.held.delete(before);
  pl.held.delete(job);
  pl.jobs.set(p.id, job);
  const i = pl.free.indexOf(p);
  if (i >= 0) pl.free.splice(i, 1);
  const j = pl.kids.indexOf(p);
  if (j >= 0) pl.kids.splice(j, 1);
  const k = pl.volunteers.indexOf(p);
  if (k >= 0) pl.volunteers.splice(k, 1);
}

/** The free workers and, for the posts elders may volunteer for, the resting retirees. */
function poolFor(pl: Planner, job: JobId): Person[] {
  return pl.sc.cfg.steward.volunteerJobs.includes(job) ? [...pl.free, ...pl.volunteers] : pl.free;
}

/** Place up to `count` people in a job; returns how many were placed. */
function takeCount(pl: Planner, job: JobId, count: number, pool = pl.free): number {
  let placed = 0;
  for (const p of ranked(pl, pool, job)) {
    if (placed >= count) break;
    place(pl, p, job);
    placed++;
  }
  return placed;
}

/** Output at k = 0 of everyone now in a job who is able to work this season. */
function provided(pl: Planner, job: JobId): number {
  let total = 0;
  for (const p of holders(pl, job)) if (ableNow(p)) total += rate(pl.sc, p, job, 0);
  return total;
}

/** Place people in a job until its output this season reaches `target`. */
function takeUntil(pl: Planner, job: JobId, target: number, pool = pl.free): number {
  let have = provided(pl, job);
  for (const p of ranked(pl, pool, job)) {
    if (have >= target) break;
    const r = rate(pl.sc, p, job, 0);
    if (r <= 0) continue;
    place(pl, p, job);
    have += r;
  }
  return have;
}

// ---------------------------------------------------------------- the essentials

function farmers(pl: Planner): void {
  const { sc } = pl;
  if (seasonOf(sc, 0) !== 'spring') return;
  const s = sc.state;
  const seedOnHand = s.seedReserved + s.stocks.food.amount;
  const bySeed = Math.floor(seedOnHand / sc.cfg.jobs.farmer.seedPerPlot + 1e-9);
  const plots = Math.min(totalPlots(sc), bySeed, Math.max(1, plotsWanted(sc)));
  const farmers = Math.ceil(plots / sc.cfg.jobs.farmer.plotsPerFarmer);
  const already = holders(pl, 'farmer').length;
  takeCount(pl, 'farmer', Math.max(0, farmers - already));
}

/** Water to carry this season: demand with a margin, less part of the stock on hand. */
function waterTarget(sc: StewardCtx): number {
  const w = sc.cfg.steward;
  return Math.max(
    0,
    sc.waterDemand * w.waterMargin - w.waterStockCredit * sc.state.stocks.water.amount,
  );
}

function water(pl: Planner, pool = pl.free): void {
  takeUntil(pl, 'water-carrier', waterTarget(pl.sc), pool);
  // M7: a second carrier learns from a master carrier when nobody else could carry on (the
  // Section 20 knowledge condition names water; a well leaves one carrier doing it all).
  if (pool === pl.free && needsSuccessor(pl.sc, 'water-carrier'))
    takeCount(pl, 'water-carrier', Math.max(0, 2 - holders(pl, 'water-carrier').length), pool);
}

/** Farm food in season k from this plan's farmers (their planted or to-be-planted plots). */
function farmFood(pl: Planner, k: number): number {
  const { sc } = pl;
  const season = seasonOf(sc, k);
  if (season !== 'summer' && season !== 'autumn') return 0;
  const startIndex = sc.seasons[0]?.seasonIndex ?? 0;
  const sameYear = startIndex + k < sc.cfg.seasons.order.length;
  const farming = holders(pl, 'farmer');
  if (sameYear) {
    let total = 0;
    const spring = seasonOf(sc, 0) === 'spring';
    for (const p of farming) {
      const plots = spring ? sc.cfg.jobs.farmer.plotsPerFarmer : (sc.state.plantedPlots[p.id] ?? 0);
      total += rate(sc, p, 'farmer', k) * plots;
    }
    return total;
  }
  // Next year's harvest: assume the plots are planted again by the best farmers available.
  const pool = [...farming, ...pl.free];
  const next = ranked(pl, pool, 'farmer').slice(0, totalPlots(sc));
  let total = 0;
  for (const p of next) total += rate(sc, p, 'farmer', k);
  return total;
}

function gatherFood(pl: Planner, k: number): number {
  const { sc } = pl;
  const dim = sc.cfg.jobs.forager.diminishing;
  let total = 0;
  let n = 0;
  const foragers = holders(pl, 'forager');
  for (const p of foragers) {
    if (k === 0 && !ableNow(p)) continue;
    let factor = 1;
    for (let i = 0; i < n; i++) factor *= dim;
    total += rate(sc, p, 'forager', k) * factor;
    n++;
  }
  const hunters = holders(pl, 'hunter');
  for (const p of hunters) if (k > 0 || ableNow(p)) total += rate(sc, p, 'hunter', k);
  return total;
}

/** Food shortfall over the horizon and the stock left at its end. */
export function foodOutlook(
  pl: Planner,
  seasons = pl.sc.horizon,
  bufferSeasons = pl.sc.cfg.steward.foodBufferSeasons,
) {
  const { sc } = pl;
  const cfg = sc.cfg;
  const spoil = num(sc.ctx, { kind: 'spoilage', resource: 'food' });
  const cap = capacityOf(sc, 'food');
  const seedNeed = totalPlots(sc) * cfg.jobs.farmer.seedPerPlot;
  let seed = sc.state.seedReserved;
  let stock = sc.state.stocks.food.amount;
  let short = 0;
  let buffer = 0;
  for (let k = 0; k < Math.min(seasons, sc.horizon); k++) {
    const produced = farmFood(pl, k) + gatherFood(pl, k);
    const step = carry(
      stock,
      produced,
      sc.foodDemand,
      cap,
      spoil,
      cfg.resources.overCapacitySpoilageMul,
    );
    short += step.short;
    stock = step.next;
    const season = seasonOf(sc, k);
    if (season === 'autumn') {
      const moved = Math.min(stock, Math.max(0, seedNeed - seed));
      seed += moved;
      stock -= moved;
    } else if (season === 'spring') seed = Math.max(0, seed - seedNeed);
    buffer += Math.max(0, sc.foodDemand * bufferSeasons - stock);
  }
  return { short, buffer, end: stock };
}

/** One more forager from `pool` if they would gather at least the marginal minimum. */
function gatherFrom(pl: Planner, pool: Person[]): boolean {
  const { sc } = pl;
  const person = ranked(pl, pool, 'forager')[0];
  if (person === undefined) return false;
  let marginal = rate(sc, person, 'forager', 0);
  for (let i = 0; i < holders(pl, 'forager').length; i++)
    marginal *= sc.cfg.jobs.forager.diminishing;
  if (marginal < sc.cfg.steward.minMarginalFood) return false;
  place(pl, person, 'forager');
  return true;
}

/** Food gatherers until the outlook over `seasons` has no shortfall (and the buffer, if any). */
function food(pl: Planner, seasons: number, bufferSeasons: number): void {
  const { sc } = pl;
  const w = sc.cfg.steward;
  for (let guard = 0; guard < sc.pop; guard++) {
    const outlook = foodOutlook(pl, seasons, bufferSeasons);
    if (outlook.short <= 1e-6 && outlook.buffer <= 1e-6) return;
    const forager = ranked(pl, pl.free, 'forager')[0];
    const hunter = ranked(pl, pl.free, 'hunter')[0];
    if (forager === undefined && hunter === undefined) return;
    let job: JobId = 'forager';
    let person = forager;
    const hunterCap = Math.max(1, Math.floor(sc.pop * w.maxHunterShare));
    if (hunter !== undefined && holders(pl, 'hunter').length < hunterCap) {
      const n = holders(pl, 'forager').length;
      let factor = 1;
      for (let i = 0; i < n; i++) factor *= sc.cfg.jobs.forager.diminishing;
      const forage = forager === undefined ? 0 : rate(sc, forager, 'forager', 0) * factor;
      if (forage < w.foragerOverHunter * rate(sc, hunter, 'hunter', 0)) {
        job = 'hunter';
        person = hunter;
      }
    }
    if (person === undefined) return;
    // M7: a gatherer who would add less than this is better building, cutting wood or resting
    // (a Winter forager gathers a fifth of a Summer one, Section 4).
    const n = holders(pl, 'forager').length;
    let marginal = rate(sc, person, job, 0);
    if (job === 'forager') for (let i = 0; i < n; i++) marginal *= sc.cfg.jobs.forager.diminishing;
    if (marginal < w.minMarginalFood) return;
    place(pl, person, job);
  }
}

/**
 * Wood over the horizon: warmth by season plus, this season, the materials `owed` by the build
 * queue. `buffer` is how far the stock falls below a share of the next season's warmth.
 */
function woodOutlook(pl: Planner, owed: number): { short: number; buffer: number; end: number } {
  const { sc } = pl;
  const cfg = sc.cfg;
  const spoil = num(sc.ctx, { kind: 'spoilage', resource: 'wood' });
  const cap = capacityOf(sc, 'wood');
  let stock = sc.state.stocks.wood.amount;
  let short = 0;
  let buffer = 0;
  for (let k = 0; k < sc.horizon; k++) {
    let produced = 0;
    for (const p of holders(pl, 'woodcutter'))
      if (k > 0 || ableNow(p)) produced += rate(sc, p, 'woodcutter', k);
    const demand = warmthDemand(sc, k) + (k === 0 ? owed : 0);
    const step = carry(stock, produced, demand, cap, spoil, cfg.resources.overCapacitySpoilageMul);
    short += step.short;
    stock = step.next;
    const want = warmthDemand(sc, k + 1) * cfg.steward.woodBufferSeasons;
    buffer += Math.max(0, want - stock);
  }
  return { short, buffer, end: stock };
}

function wood(pl: Planner, owed: number): void {
  for (let guard = 0; guard < pl.sc.pop; guard++) {
    const outlook = woodOutlook(pl, owed);
    if (outlook.short <= 1e-6 && outlook.buffer <= 1e-6) return;
    if (takeCount(pl, 'woodcutter', 1) === 0) return;
  }
}

// ---------------------------------------------------------------- the other posts

/** Builders when an order can take points, and a quarrier when the queue lacks stone. */
function construction(pl: Planner, builds: BuildNeeds): void {
  const { sc } = pl;
  const w = sc.cfg.steward;
  const pop = sc.pop;
  if (builds.buildable) {
    const base = Math.min(w.maxBuilders, Math.max(1, Math.floor(pop / w.peoplePerBuilder)));
    // M7: two builders while a lone master builder should train a successor (Section 11).
    const want = needsSuccessor(sc, 'builder') ? Math.max(2, base) : base;
    takeCount(pl, 'builder', Math.max(0, want - holders(pl, 'builder').length));
  } else if (holders(pl, 'builder').length === 0 && bestSkill(sc, 'builder') < w.keepSkillBelow) {
    // M7: with nothing to build, one builder keeps the craft alive (maintenance) while nobody
    // holds it at the Section 20 knowledge level with a margin: skill decays when unused.
    takeCount(pl, 'builder', 1);
  }
  // M7: stone for the queue, the wanted building, and a reserve for tools and upkeep once the
  // settlement is big enough to keep a toolmaker (Section 10: tools decay without one).
  const reserve = pop >= w.toolmakerMinPopulation ? w.stoneReserve : 0;
  const stone = builds.owed.stone + builds.wanted.stone + reserve;
  if (stone > sc.state.stocks.stone.amount && holders(pl, 'quarrier').length === 0)
    takeCount(pl, 'quarrier', pop >= 60 ? 2 : 1);
}

/**
 * A toolmaker while tools on hand are below the share wanted per worker and there is wood and
 * stone (or a quarrier) to make them: without one the plough, axe and bucket wear out within
 * ten seasons (Section 10), which costs a third of the harvest.
 */
function toolmaker(pl: Planner): void {
  const { sc } = pl;
  const s = sc.state;
  const canMake =
    s.stocks.wood.amount >= 2 && (s.stocks.stone.amount >= 1 || holders(pl, 'quarrier').length > 0);
  if (
    sc.pop >= sc.cfg.steward.toolmakerMinPopulation &&
    toolsLow(sc) &&
    canMake &&
    holders(pl, 'toolmaker').length === 0
  )
    takeCount(pl, 'toolmaker', 1);
}

/**
 * Effective tools (on hand times their mean durability, Section 10) below the share wanted per
 * worker who uses one. M7: the fungible stock keeps its count until the mean durability reaches
 * 0 and then vanishes at once (ADR-0009), so the count alone hides the wear.
 */
function toolsLow(sc: StewardCtx): boolean {
  const toolUsers = sc.people.filter(
    (p) => p.job !== 'rest' && sc.ctx.registry.jobs[p.job].toolFactor !== null,
  ).length;
  const effective = sc.state.stocks.tools.amount * sc.state.toolDurability;
  return effective < toolUsers * sc.cfg.steward.toolsPerWorker;
}

/**
 * A healer whenever the settlement is large enough or anyone is sick, two when the sick outnumber
 * one healer's patients: care, and the Section 20 knowledge condition (a healer at skill 3+)
 * needs a healer who keeps practising.
 */
function healer(pl: Planner): void {
  const { sc } = pl;
  const w = sc.cfg.steward;
  const sick = sc.people.filter((p) => p.status.sick !== null || p.status.injured !== null).length;
  if (sc.pop < w.healerMinPopulation && sick === 0) return;
  // M7: a second healer learns from a master when nobody else could carry on (Section 11).
  const want = sick > sc.cfg.jobs.healer.treats || needsSuccessor(sc, 'healer') ? 2 : 1;
  takeCount(pl, 'healer', Math.max(0, want - holders(pl, 'healer').length), poolFor(pl, 'healer'));
}

/** The settlement's highest skill in a job. */
function bestSkill(sc: StewardCtx, job: JobId): number {
  let best = 0;
  for (const p of sc.people) best = Math.max(best, skillOf(p, job));
  return best;
}

/**
 * A master (skill 4+, Section 11) holds the skill and nobody else in the settlement has it at
 * the Section 20 knowledge level: a successor should learn from them while they live.
 */
export function needsSuccessor(sc: StewardCtx, job: JobId): boolean {
  const master = sc.cfg.skills.masterMinSkill;
  const keep = sc.cfg.steady.knowledgeMinSkill;
  let masters = 0;
  let others = 0;
  for (const p of sc.people) {
    const level = skillOf(p, job);
    if (level >= master) masters++;
    if (level >= keep) others++;
  }
  return masters === 1 && others === 1;
}

/**
 * Section 11 apprenticeships in the planned crew (M7): in the jobs listed in
 * `steward.apprenticeJobs`, a planned worker below the knowledge level learns from a planned
 * master in the same job (at most `skills.maxApprentices` each); an apprenticeship whose
 * apprentice has reached the knowledge level, or no longer shares the master's job, is ended so
 * the master's 0.85x lifts.
 */
export function apprenticeships(sc: StewardCtx, jobs: Map<PersonId, JobId>) {
  const out: Record<PersonId, PersonId | null> = {};
  const keep = sc.cfg.steady.knowledgeMinSkill;
  const taken = new Map<PersonId, number>();
  const byId = new Map(sc.people.map((p) => [p.id, p]));
  for (const p of sc.people) {
    if (p.masterId === null) continue;
    const job = jobs.get(p.id);
    const master = byId.get(p.masterId);
    const done = job === undefined || skillOf(p, job) >= keep;
    if (master === undefined || done || jobs.get(master.id) !== job) out[p.id] = null;
    else taken.set(master.id, (taken.get(master.id) ?? 0) + 1);
  }
  for (const job of sc.cfg.steward.apprenticeJobs as JobId[]) {
    const holders = sc.people.filter((p) => jobs.get(p.id) === job && !isKid(sc, p));
    const masters = holders
      .filter((p) => skillOf(p, job) >= sc.cfg.skills.masterMinSkill)
      .sort((a, b) => skillOf(b, job) - skillOf(a, job) || (a.id < b.id ? -1 : 1));
    for (const learner of holders) {
      if (skillOf(learner, job) >= keep || (learner.masterId !== null && out[learner.id] !== null))
        continue;
      const master = masters.find(
        (m) =>
          m.id !== learner.id &&
          skillOf(m, job) > skillOf(learner, job) &&
          (taken.get(m.id) ?? 0) < sc.cfg.skills.maxApprentices,
      );
      if (master === undefined) continue;
      out[learner.id] = master.id;
      taken.set(master.id, (taken.get(master.id) ?? 0) + 1);
    }
  }
  return out;
}

function others(pl: Planner): void {
  const { sc } = pl;
  const w = sc.cfg.steward;
  const s = sc.state;
  const pupils = sc.people.filter((p) => {
    const years = Math.floor(p.ageSeasons / sc.cfg.people.seasonsPerYear);
    return years >= sc.cfg.skills.schoolAgeFromYears && years <= sc.cfg.skills.schoolAgeToYears;
  }).length;
  if (
    buildingCount(s, 'school') > 0 &&
    s.policies.schooling !== 'none' &&
    pupils >= w.schoolMinChildren &&
    holders(pl, 'teacher').length === 0
  )
    takeCount(pl, 'teacher', 1, poolFor(pl, 'teacher'));
  const infants = sc.people.filter(
    (p) => p.ageSeasons < sc.cfg.lifecycle.caretakerUnderYears * sc.cfg.people.seasonsPerYear,
  ).length;
  if (infants >= w.minCaretakerChildren) {
    const want = Math.ceil(infants / w.childrenPerCaretaker);
    takeCount(
      pl,
      'caretaker',
      Math.max(0, want - holders(pl, 'caretaker').length),
      poolFor(pl, 'caretaker'),
    );
  }
}

/**
 * Guards (Section 13), kept steady (a safety shortage costs trust each time it starts, D-36):
 * guards until their safety points (their Section 8 output, so skill, strength, a spear and a
 * watch post count) and the buildings' cover the Section 6 need (population / 15) with a
 * margin, and at least two to fight a raid when there are outside threats; one in the first
 * year (the settlement feeds and houses itself first); none below `guardMinPopulation`.
 */
function guards(pl: Planner): void {
  const { sc } = pl;
  const w = sc.cfg.steward;
  if (sc.pop < w.guardMinPopulation) return;
  if (sc.state.year < w.guardFromYear) {
    // The first year: one guard on watch while the settlement feeds and houses itself.
    takeCount(pl, 'guard', Math.max(0, 1 - holders(pl, 'guard').length));
    return;
  }
  const buildings = Math.max(0, num(sc.ctx, { kind: 'service', service: 'safetyPoints' }));
  const needed = (sc.pop / sc.cfg.threats.peoplePerSafetyPoint) * w.safetyMargin - buildings;
  takeUntil(pl, 'guard', needed);
  const threats = sc.cfg.threatLevel[sc.state.options.threatLevel] > 0;
  if (threats && sc.state.year >= w.guardFromYear)
    takeCount(pl, 'guard', Math.max(0, w.raidGuards - holders(pl, 'guard').length));
}

/** Stocks the gathering jobs add to (the check that more of them is not wasted). */
const STOCK_JOB: Partial<Record<JobId, Resource>> = {
  'water-carrier': 'water',
  forager: 'food',
  hunter: 'food',
  woodcutter: 'wood',
  quarrier: 'stone',
};

/** The order leftover workers are offered jobs in. Never hunting: it kills (Section 8). */
const LEFTOVER_JOBS: readonly JobId[] = [
  'forager',
  'woodcutter',
  'water-carrier',
  'quarrier',
  'guard',
  'healer',
];

interface Headroom {
  /** Room left this season in each stock before production spills over capacity. */
  room: Partial<Record<Resource, number>>;
  foragers: number;
}

function headroom(pl: Planner, owed: number): Headroom {
  const { sc } = pl;
  const s = sc.state;
  const produced: Partial<Record<Resource, number>> = {};
  for (const [job, r] of Object.entries(STOCK_JOB) as [JobId, Resource][])
    produced[r] = (produced[r] ?? 0) + provided(pl, job);
  produced.food = (produced.food ?? 0) + farmFood(pl, 0);
  const room = (r: Resource, use: number): number =>
    capacityOf(sc, r) + use - s.stocks[r].amount - (produced[r] ?? 0);
  return {
    room: {
      water: room('water', sc.waterDemand),
      food: room('food', sc.foodDemand),
      wood: room('wood', warmthDemand(sc, 0) + owed),
      stone: room('stone', 0),
    },
    foragers: holders(pl, 'forager').length,
  };
}

/** Would one more of `p` in `job` add something this season (not wasted, not idle)? */
function useful(pl: Planner, h: Headroom, p: Person, job: JobId, builds: BuildNeeds): number {
  const { sc } = pl;
  const w = sc.cfg.steward;
  if (!canWork(sc.ctx, p, job)) return 0;
  const r = STOCK_JOB[job];
  if (r !== undefined) {
    // No room for more of it this season: nothing useful, whatever the rate (skip evaluating it).
    if ((h.room[r] ?? 0) <= 1e-9) return 0;
    let out = rate(sc, p, job, 0);
    if (job === 'forager')
      for (let i = 0; i < h.foragers; i++) out *= sc.cfg.jobs.forager.diminishing;
    if (job === 'forager' && out < w.leftoverMinYieldShare * rate(sc, p, job, 0)) return 0;
    return out > 1e-9 && (h.room[r] ?? 0) >= out ? out : 0;
  }
  switch (job) {
    case 'builder':
      return builds.buildable && holders(pl, 'builder').length < w.maxBuilders * 2 ? 1 : 0;
    case 'guard': {
      const needed = Math.ceil(sc.pop / sc.cfg.threats.peoplePerSafetyPoint);
      return holders(pl, 'guard').length < needed ? 1 : 0;
    }
    case 'healer': {
      const medicine = sc.state.stocks.medicine;
      const want = Math.max(1, Math.ceil(sc.pop / (w.healerMinPopulation * 3)));
      return holders(pl, 'healer').length < want && medicine.amount < medicine.capacity ? 1 : 0;
    }
    case 'toolmaker':
      return holders(pl, 'toolmaker').length < 2 &&
        sc.state.stocks.tools.amount < sc.state.stocks.tools.capacity
        ? 1
        : 0;
    default:
      return 0;
  }
}

function placeLeftover(pl: Planner, h: Headroom, p: Person, job: JobId, out: number): void {
  place(pl, p, job);
  const r = STOCK_JOB[job];
  if (r !== undefined) h.room[r] = (h.room[r] ?? 0) - out;
  if (job === 'forager') h.foragers++;
}

/**
 * Whoever is left. First more food while the stock is thin, more wood below half capacity and
 * a spare builder; then everyone else keeps their job if it still adds something this season,
 * or takes the first useful one (idle adults lose trust, Section 8); the rest rest.
 */
function flex(pl: Planner, builds: BuildNeeds, owed: number): void {
  const { sc } = pl;
  const w = sc.cfg.steward;
  let extraFood = 0;
  let extraWood = 0;
  let extraBuilders = 0;
  while (pl.free.length > 0) {
    const outlook = foodOutlook(pl);
    if (outlook.end < sc.foodDemand * 2 && extraFood < 3) {
      if (takeCount(pl, 'forager', 1) === 1) {
        extraFood++;
        continue;
      }
    }
    const woodEnd = woodOutlook(pl, owed).end;
    if (woodEnd < capacityOf(sc, 'wood') / 2 && extraWood < 2) {
      if (takeCount(pl, 'woodcutter', 1) === 1) {
        extraWood++;
        continue;
      }
    }
    if (builds.buildable && extraBuilders < 1 && holders(pl, 'builder').length < w.maxBuilders) {
      if (takeCount(pl, 'builder', 1) === 1) {
        extraBuilders++;
        continue;
      }
    }
    break;
  }
  const h = headroom(pl, owed);
  for (const p of pl.free.slice()) {
    if (p.job === 'rest') continue;
    const out = useful(pl, h, p, p.job, builds);
    if (out > 0) placeLeftover(pl, h, p, p.job, out);
  }
  for (const p of pl.free.slice()) {
    for (const job of LEFTOVER_JOBS) {
      const out = useful(pl, h, p, job, builds);
      if (out <= 0) continue;
      placeLeftover(pl, h, p, job, out);
      break;
    }
  }
  for (const p of pl.free.slice()) place(pl, p, 'rest');
}

/** Children cover essentials only when the adults cannot (Section 18 child labour). */
function emergency(pl: Planner): void {
  // M7: resting retirees are asked to volunteer for water and gathering before any child works
  // (Section 15: elders can do any non-heavy job; Section 18: past retirement they may volunteer).
  if (pl.volunteers.length > 0) {
    water(pl, pl.volunteers);
    for (let guard = 0; guard < pl.sc.pop && pl.volunteers.length > 0; guard++) {
      if (foodOutlook(pl, pl.sc.cfg.steward.foodPlanSeasons, 0).short <= 1e-6) break;
      if (!gatherFrom(pl, pl.volunteers)) break;
    }
  }
  if (pl.kids.length === 0) return;
  water(pl, pl.kids);
  for (let guard = 0; guard < 2 && pl.kids.length > 0; guard++) {
    if (foodOutlook(pl, pl.sc.cfg.steward.foodPlanSeasons, 0).short <= 1e-6) break;
    if (takeCount(pl, 'forager', 1, pl.kids) === 0) break;
  }
  if (pl.kids.length > 0 && woodOutlook(pl, 0).short > 1e-6)
    takeCount(pl, 'woodcutter', 1, pl.kids);
}

// ---------------------------------------------------------------- the plan

function initialPlanner(sc: StewardCtx, variant: Variant): Planner {
  const jobs = new Map<PersonId, JobId>();
  const free: Person[] = [];
  const kids: Person[] = [];
  const volunteers: Person[] = [];
  const midYear = seasonOf(sc, 0) === 'summer' || seasonOf(sc, 0) === 'autumn';
  const restBelow = sc.cfg.steward.restBelowStrength;
  for (const p of sc.people) {
    if (isKid(sc, p)) {
      jobs.set(p.id, 'rest');
      if (canWork(sc.ctx, p, 'forager') && ableNow(p)) kids.push(p);
      continue;
    }
    if (!ableNow(p)) {
      jobs.set(p.id, canWork(sc.ctx, p, p.job) ? p.job : 'rest');
      continue;
    }
    if (midYear && p.job === 'farmer' && (sc.state.plantedPlots[p.id] ?? 0) > 0) {
      jobs.set(p.id, 'farmer');
      continue;
    }
    if (isRetired(sc, p)) {
      // Volunteers keep a post they can still do; a resting retiree may be asked to volunteer
      // for a light, skilled post (healer, teacher, caretaker), else rests.
      if (p.job !== 'rest' && canWork(sc.ctx, p, p.job)) jobs.set(p.id, p.job);
      else {
        jobs.set(p.id, 'rest');
        volunteers.push(p);
      }
      continue;
    }
    if (variant === 'recover' && p.strength < restBelow) {
      jobs.set(p.id, 'rest');
      continue;
    }
    free.push(p);
  }
  return { sc, variant, jobs, free, kids, volunteers, held: new Map() };
}

export function planCrew(sc: StewardCtx, variant: Variant, builds: BuildNeeds): Plan {
  const pl = initialPlanner(sc, variant);
  const w = sc.cfg.steward;
  const owed = builds.owed.wood + builds.wanted.wood;
  const coldSeason = seasonOf(sc, 0) === 'autumn' || seasonOf(sc, 0) === 'winter';
  const cautious = variant === 'essentials';
  const foodSeasons = cautious ? sc.horizon : w.foodPlanSeasons;
  const foodBuffer = cautious ? w.foodBufferSeasons : 0;
  // Every variant covers this season's water first (dehydration kills fastest, Section 6),
  // then plants the plots the year's food wants with whoever is left (Section 8).
  // M7: the healer first (one person, the most skilled, who keeps practising: care, and the
  // Section 20 knowledge condition), then water and the planting; the building crew comes before
  // extra gatherers in every plan but the cautious one: the granary, the well and beds are what
  // carry a settlement past its first Winter.
  healer(pl);
  water(pl);
  if (variant === 'invest') construction(pl, builds);
  farmers(pl);
  if (coldSeason) wood(pl, 0);
  if (variant !== 'essentials') {
    construction(pl, builds);
    toolmaker(pl);
    guards(pl);
  }
  food(pl, foodSeasons, foodBuffer);
  wood(pl, owed);
  if (variant !== 'essentials') others(pl);
  emergency(pl);
  flex(pl, variant === 'essentials' ? { ...builds, buildable: false } : builds, owed);

  const short: Plan['short'] = [];
  if (provided(pl, 'water-carrier') + sc.state.stocks.water.amount < sc.waterDemand - 1e-6)
    short.push('water');
  if (foodOutlook(pl).short > 1e-6) short.push('food');
  if (woodOutlook(pl, 0).short > 1e-6) short.push('warmth');
  return { variant, jobs: pl.jobs, risk: short.length > 0, short };
}

/** Workers weak enough that resting them is worth comparing (Section 20 weakness term). */
export function weakShare(sc: StewardCtx): number {
  const workers = sc.people.filter((p) => isWorking(p));
  if (workers.length === 0) return 0;
  const below = sc.cfg.fragility.weakStrengthBelow;
  return workers.filter((p) => p.strength < below).length / workers.length;
}

export function hasWeakFree(sc: StewardCtx): boolean {
  const restBelow = sc.cfg.steward.restBelowStrength;
  return sc.people.some(
    (p) => !isKid(sc, p) && !isRetired(sc, p) && ableNow(p) && p.strength < restBelow,
  );
}
